import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { API_ENDPOINTS } from '../../config/api'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { GovernanceListingSection } from '../governance/GovernanceListingSection'
import { ComplianceCenterMetricTile } from '../compliance-center/ComplianceCenterMetricTile'
import { ComplianceCenterListRow } from '../compliance-center/ComplianceCenterListRow'
import { Button } from '../ui/Button'
import { getStandardDisplayName, normalizeComplianceStandardCode } from '../../utils/complianceStandard'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'

type AuditReadinessSummary = {
  readinessScore?: number
  verifiedScore?: number
  readinessStatus?: string
}

type AuditGap = {
  id: string
  title?: string
  controlCode?: string
  severity?: string
  status?: string
  standardCode?: string
}

type AuditScope = {
  id: string
  name?: string
  code?: string
}

export default function OperationalWorkflowAuditReadinessSummary() {
  const { t } = useI18n()
  const { token } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [summary, setSummary] = useState<AuditReadinessSummary>({})
  const [gaps, setGaps] = useState<AuditGap[]>([])
  const [scopes, setScopes] = useState<AuditScope[]>([])

  const load = useCallback(async () => {
    if (!token) {
      setLoading(false)
      return
    }
    setLoading(true)
    const headers = { Authorization: `Bearer ${token}` }
    try {
      const [readinessRes, gapsRes, scopesRes] = await Promise.all([
        fetch(API_ENDPOINTS.COMPLIANCE_AUDIT_READINESS, { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_AUDIT_READINESS_GAPS, { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_AUDIT_SCOPES, { headers }),
      ])
      const readinessJson = readinessRes.ok ? await readinessRes.json().catch(() => ({})) : {}
      const gapsJson = gapsRes.ok ? await gapsRes.json().catch(() => ({})) : {}
      const scopesJson = scopesRes.ok ? await scopesRes.json().catch(() => ({})) : {}
      setSummary(readinessJson?.data || {})
      setGaps(Array.isArray(gapsJson?.data) ? gapsJson.data : [])
      setScopes(Array.isArray(scopesJson?.data) ? scopesJson.data : [])
    } catch {
      setSummary({})
      setGaps([])
      setScopes([])
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    void load()
  }, [load])

  const openGapWorkflow = (gap: AuditGap) => {
    const code = gap.standardCode ? normalizeComplianceStandardCode(gap.standardCode) : null
    if (code) {
      navigate(`/compliance-workflow/${encodeURIComponent(code)}`)
      return
    }
    navigate('/compliance-workflow')
  }

  return (
    <>
      <GovernanceListingSection
        title={t('complianceCenterUi.nav.auditReadiness', 'Audit Readiness')}
        description={t(
          'complianceWorkflow.auditReadinessMergedIntro',
          'Organization readiness scores, audit scopes, and blocking gaps are managed here alongside operational workflow — scope, applicability, and certification per standard.',
        )}
      >
        {loading ? (
          <p className={listStyles.panelSubtitle}>{t('common.loading', 'Loading…')}</p>
        ) : (
          <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid4}`}>
            <ComplianceCenterMetricTile
              label={t('complianceCenterUi.readinessScore', 'Readiness score')}
              value={`${Number(summary?.readinessScore || 0)}%`}
              tone="blue"
            />
            <ComplianceCenterMetricTile
              label={t('complianceCenterUi.verifiedScore', 'Verified score')}
              value={`${Number(summary?.verifiedScore || 0)}%`}
              tone="green"
            />
            <ComplianceCenterMetricTile
              label={t('complianceCenterUi.blockingGaps', 'Blocking gaps')}
              value={gaps.length}
              tone="red"
            />
            <ComplianceCenterMetricTile
              label={t('complianceCenterUi.auditScopes', 'Audit scopes')}
              value={scopes.length}
              tone="purple"
            />
          </div>
        )}
      </GovernanceListingSection>

      {!loading ? (
        <>
          <GovernanceListingSection title={t('complianceCenterUi.configuredScopes', 'Configured scopes')}>
            <p className={listStyles.panelSubtitle}>
              {scopes.length
                ? scopes.map((scope) => scope.name || scope.code || scope.id).join(', ')
                : t('complianceCenterUi.noScopes', 'No scopes configured.')}
            </p>
          </GovernanceListingSection>

          <GovernanceListingSection title={t('complianceCenterUi.topBlockingGaps', 'Top blocking gaps')}>
            <div className={listStyles.listStack}>
              {gaps.slice(0, 10).map((gap) => (
                <ComplianceCenterListRow
                  key={gap.id}
                  title={gap.title || gap.controlCode || 'Gap'}
                  meta={`${t('common.severity', 'Severity')}: ${gap.severity || 'n/a'} · ${t('common.status', 'Status')}: ${gap.status || 'open'}${
                    gap.standardCode ? ` · ${getStandardDisplayName(t, gap.standardCode)}` : ''
                  }`}
                  actions={
                    <Button type="button" size="small" color="outline" onClick={() => openGapWorkflow(gap)}>
                      {t('complianceWorkflow.openWorkflow', 'Open workflow')}
                    </Button>
                  }
                />
              ))}
              {!gaps.length ? (
                <p className={listStyles.panelSubtitle}>{t('complianceCenterUi.noGaps', 'No gaps returned.')}</p>
              ) : null}
            </div>
          </GovernanceListingSection>
        </>
      ) : null}
    </>
  )
}
